import { DEPRECATED_REACT_PROPS, List, ListItem, Popper, TextField, Typography } from '@chronosphereio/chrono-ui';
import { useDebouncedState } from '@chronosphereio/shared';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { matchSeriesOverride, parseOverrideAlias } from '@/core';
import { RegexMatchDisplay } from './RegexMatchDisplay';

type Match = {
  name: string;
  regexPattern: RegExp;
};

export type OverrideRegexPreviewProps = {
  value: string;
  onChange: (value: string) => void;
  seriesNames: string[];
  inputRef?: RefObject<HTMLInputElement>;
};

export function OverrideRegexPreview({ value, onChange, seriesNames, inputRef }: OverrideRegexPreviewProps) {
  // Debouncing in case there are many series
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [regexText, setRegexText] = useDebouncedState(
    value,
    (newValue) => {
      setDebouncedValue(newValue);
    },
    500
  );

  const [isFocused, setIsFocused] = useState(false);
  const [regexError, setRegexError] = useState<string>();
  const [matches, setMatches] = useState<Match[]>([]);

  const anchorEl = useRef<HTMLDivElement | null>(null);

  const getMatches = useCallback(
    (inputValue: string) => {
      const matches = seriesNames.reduce<Match[]>((acc, name) => {
        try {
          const regexPattern = parseOverrideAlias(inputValue);
          const isMatch = matchSeriesOverride({ value: inputValue, name, isRegex: true });
          setRegexError(undefined);
          if (isMatch) {
            acc.push({ name, regexPattern });
          }
        } catch (e) {
          if (e instanceof Error) {
            setRegexError(e.message);
          }
        }
        return acc;
      }, []);
      setMatches(matches);
    },
    [seriesNames]
  );

  useEffect(() => {
    if (isFocused) {
      getMatches(debouncedValue);
    }
  }, [isFocused, debouncedValue, getMatches]);

  return (
    <>
      <TextField
        fullWidth
        label="Regex"
        placeholder="Ex. /production-/"
        value={regexText}
        onChange={(event) => {
          setRegexText(event.target.value);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          onChange(regexText);
          setIsFocused(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onChange(regexText);
          }
        }}
        error={!!regexError}
        helperText={regexError}
        inputRef={inputRef}
        ref={anchorEl}
      />
      <Popper
        open={isFocused && matches.length > 0}
        anchorEl={anchorEl.current}
        placement="bottom-start"
        sx={{
          // Preview should show above other controls
          zIndex: 'tooltip',
        }}
        {...DEPRECATED_REACT_PROPS}
      >
        <List
          sx={(theme) => ({
            overflow: 'auto',
            paddingY: 1,
            paddingX: 0.5,
            backgroundColor: theme.palette.background.tooltip,
            boxShadow: theme.shadows[5],
            maxHeight: 250,
            maxWidth: (anchorEl.current?.getBoundingClientRect().width || 0) * 2.5,
          })}
        >
          <ListItem>
            <Typography
              variant="overline2"
              sx={(theme) => ({
                color: theme.palette.mode === 'light' ? theme.palette.text.inversePrimary : theme.palette.text.primary,
              })}
            >
              Matching series
            </Typography>
          </ListItem>
          {matches.map((match, index) => (
            <ListItem key={`match-${index}`}>
              <RegexMatchDisplay name={match.name} regex={match.regexPattern} />
            </ListItem>
          ))}
        </List>
      </Popper>
    </>
  );
}
